import { Calendar as ReactBigCalendar, View } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "../utils/override.css";
import { ScheduleCustomEvent, ScheduleCustomEventMonth } from "./ScheduleCustomEvent";
import { Box, Fab, Tooltip } from "@mui/material";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import {
    handleCalendarDate,
    handleRanges,
    localizer,
    useEventDrop,
    useFormats,
    useHandleSelectSlot,
} from "../../../components/calendar/calendarUtils";
import { useNavigate } from "react-router-dom";
import { set, startOfDay } from "date-fns";
import { useStore } from "../../../store/store";
import { CustomEventType } from "../utils/types";
import { SelectedEventsActions } from "./schedule-actions/SelectedEventsActions";
import { customSlotPropGetter, createDayPropGetter } from "../utils/scheduleUtils";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useState } from "react";
import { WeeklyTotalsOverlay } from "./weekly-total-overlay/WeeklyTotalsOverlay";
import { useMemo } from "react";
import { CustomToolbar } from "./CustomToolbar";
import { isLightColor } from "../../../utils/isLightColor";
import { usePortugalHolidays } from "../../../api/usePortugalHolidays";

const minTime = set(new Date(), { hours: 8, minutes: 0, seconds: 0 });
const maxTime = set(new Date(), { hours: 22, minutes: 59, seconds: 59 });

const DnDCalendar = withDragAndDrop(ReactBigCalendar);

// Create wrapper components for different views
const EventWrapperDetailed = ({ event }: { event: CustomEventType }) => {
    return <ScheduleCustomEvent event={event} />;
};

const EventWrapperMonth = ({ event }: { event: CustomEventType }) => {
    return <ScheduleCustomEventMonth event={event} />;
};

const CALENDAR_COMPONENTS = {
    month: {
        event: EventWrapperMonth,
    },
    week: {
        event: EventWrapperDetailed,
    },
    day: {
        event: EventWrapperDetailed,
    },
    toolbar: CustomToolbar,
};

type SchedulesCalendarProps = {
    filteredEvents: Array<CustomEventType>;
    selectMode: boolean;
    setSelectMode: (value: boolean) => void;
    setSelectedEvents: (events: Array<CustomEventType>) => void;
    selectedEvents: Array<CustomEventType>;
    arrangedResources: any;
    users: any;
    clinics: any;
};

export const SchedulesCalendar = ({
    filteredEvents,
    users,
    setSelectedEvents,
    selectMode,
    setSelectMode,
    selectedEvents,
    arrangedResources,
    clinics,
}: SchedulesCalendarProps) => {
    const {
        schedules: { calendarFilters, setCalendarFilters, setCalendarView },
    } = useStore();

    const handleSelectSlot = useHandleSelectSlot();
    const navigate = useNavigate();
    const { formats } = useFormats("both");

    const onEventChange = useEventDrop("schedules");
    const [showWeeklyTotals, setShowWeeklyTotals] = useState(false);

    // Create a unique key that changes when resources change to force re-rendering
    const calendarKey = useMemo(() => {
        return `calendar-${arrangedResources?.length || 0}-${clinics?.length || 0}-${Date.now()}`;
    }, [arrangedResources, clinics]);

    const handleEventSelection = (event: CustomEventType) => {
        const result = selectedEvents.some((selectedEvent) => selectedEvent.id === event.id)
            ? selectedEvents.filter((selectedEvent) => selectedEvent.id !== event.id)
            : [...selectedEvents, event];

        setSelectedEvents(result);
    };

    const { data: holidays } = usePortugalHolidays(new Date());

    // Create dayPropGetter for highlighting holidays
    const dayPropGetter = useMemo(() => {
        return createDayPropGetter(holidays || []);
    }, [holidays]);

    return (
        <Box className="schedules-calendar-wrapper" sx={{ height: 1000, position: "relative" }}>
            <DnDCalendar
                key={calendarKey} // Use a dynamic key to force re-render when resources change
                //@ts-ignore
                components={CALENDAR_COMPONENTS}
                resources={arrangedResources}
                //@ts-ignore
                resourceIdAccessor="id"
                //@ts-ignore
                resourceTitleAccessor="title"
                //@ts-ignore
                resourceAccessor="room"
                resourceGroupingLayout={true}
                localizer={localizer}
                culture="pt-PT"
                events={filteredEvents}
                date={calendarFilters?.dateRange && handleCalendarDate(calendarFilters.dateRange)}
                onNavigate={(date, view) => {
                    const newFilters = {
                        ...calendarFilters,
                        dateRange: handleRanges(view === "day" ? [date] : [date]),
                    };
                    setCalendarFilters(newFilters);
                }}
                // @ts-ignore
                startAccessor="start"
                // @ts-ignore
                endAccessor="end"
                defaultView="week"
                min={minTime}
                max={maxTime}
                timeslots={4}
                resourcesWidth={10}
                step={15}
                views={["day", "week", "month"]}
                selectable={true}
                onSelectSlot={(e) => {
                    handleSelectSlot({
                        start: e.start,
                        end: e.end,
                        entity: "schedules",
                        defaultMinutes: 240,
                        roomId: e.resourceId as number,
                    });
                }}
                onSelectEvent={(event: any) => {
                    if (selectMode) {
                        handleEventSelection(event);
                        return;
                    }

                    const entity = event.type === "schedule" ? "schedules" : "time_off";
                    navigate(`/${entity}/${event.id}/update`);
                }}
                eventPropGetter={(event: any) => {
                    const isSelected = selectedEvents.some((selectedEvent) => selectedEvent.id === event.id);

                    return {
                        style: {
                            backgroundColor: event?.userColor,
                            opacity: selectMode ? (isSelected ? 1 : 0.7) : 1,
                            border: isSelected ? "2px solid black" : "none",
                            color: isLightColor(event?.userColor) ? "#404040" : "#fff",
                        },
                    };
                }}
                // @ts-ignore
                formats={formats}
                slotPropGetter={customSlotPropGetter}
                dayPropGetter={dayPropGetter}
                draggableAccessor={(event) => true}
                onEventDrop={onEventChange}
                resizable
                onEventResize={onEventChange}
                onRangeChange={(range: any, view: View | undefined) => {
                    const newFilters = {
                        ...calendarFilters,
                        dateRange: handleRanges(view === "day" ? [startOfDay(new Date())] : range),
                    };

                    setCalendarFilters(newFilters);
                }}
                dayLayoutAlgorithm="no-overlap"
                onView={(view: View) => {
                    setCalendarView(view as any);
                }}
                popup
            />
            {calendarFilters?.dateRange && (
                <WeeklyTotalsOverlay
                    open={showWeeklyTotals}
                    events={filteredEvents}
                />
            )}
            <Tooltip title="Totais semanais">
                <Fab
                    size="large"
                    color={showWeeklyTotals ? "secondary" : "primary"}
                    onClick={() => setShowWeeklyTotals((v) => !v)}
                    sx={{ position: "absolute", right: 12, bottom: 12, zIndex: 11 }}
                >
                    <SummarizeIcon fontSize="small" />
                </Fab>
            </Tooltip>
            <SelectedEventsActions
                selectedEvents={selectedEvents}
                users={users}
                setSelectedEvents={setSelectedEvents}
                setSelectMode={setSelectMode}
                selectMode={selectMode}
                filteredEvents={filteredEvents}
                resources={arrangedResources}
                clinics={clinics}
                currentDate={calendarFilters?.dateRange?.start_date ? new Date(calendarFilters.dateRange.start_date) : new Date()}
            />
        </Box>
    );
};
